import { useEffect, useState } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
let cache = null;
let inflight = null;

export const normalizeCategory = (name) =>
  String(name || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase().trim().replace(/\s+/g, " ");

function buildIndex(data) {
  const byNorm = {};
  Object.keys(data.categories || {}).forEach((k) => { byNorm[normalizeCategory(k)] = k; });
  return { ...data, byNorm };
}

export function loadSpecSchemas() {
  if (cache) return Promise.resolve(cache);
  if (!inflight) {
    inflight = axios.get(`${API}/spec-schemas`).then((r) => { cache = buildIndex(r.data); return cache; })
      .catch(() => { cache = buildIndex({ categories: {}, aliases: {}, equipment_types: {}, legacy_keys: {} }); return cache; });
  }
  return inflight;
}

export const resolveCategory = (data, name) => {
  if (!data) return null;
  const n = normalizeCategory(name);
  if (!n) return null;
  return data.byNorm[n] || data.aliases?.[n] || null;
};

export const schemaFor = (data, category) => {
  const canon = resolveCategory(data, category);
  return canon ? (data.categories[canon]?.fields || []) : [];
};

const filled = (v) => v !== null && v !== undefined && v !== false && String(v).trim() !== "";

export const requiredMissing = (data, category, specs) =>
  schemaFor(data, category).filter((f) => f.required && !filled((specs || {})[f.key]));

export const specProgress = (data, category, specs) => {
  const req = schemaFor(data, category).filter((f) => f.required);
  const ok = req.filter((f) => filled((specs || {})[f.key])).length;
  return { total: req.length, filled: ok, complete: req.length === 0 || ok === req.length, hasSchema: schemaFor(data, category).length > 0 };
};

export const humanizeKey = (k) => String(k || "").replace(/_/g, " ").replace(/^\w/, (c) => c.toUpperCase());

// Ordena las specs de un producto según el esquema y agrega las extra al final.
export const orderedSpecs = (data, category, specs) => {
  const s = specs || {};
  const schema = schemaFor(data, category);
  const out = [];
  const seen = new Set();
  for (const f of schema) {
    if (filled(s[f.key])) { out.push({ key: f.key, label: f.label, value: String(s[f.key]), highlight: !!f.highlight }); seen.add(f.key); }
  }
  for (const [k, v] of Object.entries(s)) {
    if (!seen.has(k) && filled(v)) out.push({ key: k, label: humanizeKey(k), value: String(v), highlight: false });
  }
  return out;
};

export function useSpecSchemas() {
  const [data, setData] = useState(cache);
  useEffect(() => { if (!cache) loadSpecSchemas().then(setData); }, []);
  return {
    data,
    ready: !!data,
    schemaFor: (category) => schemaFor(data, category),
    requiredMissing: (category, specs) => requiredMissing(data, category, specs),
    progress: (category, specs) => specProgress(data, category, specs),
    ordered: (category, specs) => orderedSpecs(data, category, specs),
    equipmentCategory: (type) => data?.equipment_types?.[type] || null,
    legacyKeys: data?.legacy_keys || {},
  };
}
